import { type Maybe, day, ensure, hasValue, isUUID } from '@vrplatform/utils';
import type {
  listing_bool_exp,
  reservation_bool_exp,
  source_bool_exp,
} from '@vrplatform/graphql';
import {
  whereGlListingIsInactive,
  whereListingIsActive,
} from '@vrplatform/graphql';
import { z } from 'zod';
import type { Dashboard } from '../types';
import { whereConnectionStatusDefault } from './whereConnectionStatusDefault';
import { whereLegacyListingIsExcluded } from './whereLegacyListingIsExcluded';

export const reservationsFilterInput = {
  connectionIds: z.array(z.string()).optional(),
  date: z.tuple([z.date().nullable(), z.date().nullable()]).optional(),
  dateType: z
    .enum(['all', 'checkIn', 'checkOut', 'bookedAt', 'cancelledAt'])
    .optional(),
  bookingChannels: z.array(z.string()).optional(),
  search: z.string().optional(),
  status: z.string().optional(),
  paidStatus: z.enum(['paid', 'unpaid']).optional(),
  listingCollectionId: z.string().nullish(),
  listingIds: z.array(z.string()).optional(),
  listingStatus: z.enum(['active', 'inactive']).optional(),
  onlyEnabledListingByAutomationId: z.string().optional(),
};

const reservationsFilter = z.object(reservationsFilterInput);

export type ReservationsFilterParams = z.infer<typeof reservationsFilter>;

type BaseWhere = {
  dashboard: Dashboard;
  tenantId: string;
  includeLines: boolean;
  ownerPortalContactId: string | null;
  GL: boolean;
};

type Props = BaseWhere & ReservationsFilterParams;

export function whereReservations({
  date,
  search: s,
  listingIds,
  tenantId,
  dashboard,
  bookingChannels: bc,
  connectionIds,
  dateType,
  onlyEnabledListingByAutomationId,
  includeLines,
  status: st,
  paidStatus,
  listingStatus,
  listingCollectionId,
  GL,
  ownerPortalContactId,
}: Props) {
  const status = st as Maybe<'booked' | 'canceled' | 'deleted' | 'cancelled'>;
  const search = s?.trim();

  const listing = getListingBool({
    dashboard,
    listingIds,
    listingStatus,
    listingCollectionId,
    GL,
    ownerPortalContactId,
  });

  const _and: reservation_bool_exp[] = listing ? [listing] : [];

  if (search) {
    _and.push({
      _or: [
        {
          guestName: { _ilike: `%${search}%` },
        },
        {
          uniqueRef: { _ilike: `%${search}%` },
        },
        {
          confirmationCode: { _ilike: `%${search}%` },
        },
        {
          pmsReferenceCode: { _ilike: `%${search}%` },
        },
        isUUID(search) ? { id: { _eq: search } } : undefined,
      ].filter(hasValue),
    });
  }

  if (date) {
    const d = getDateBool(date, dateType);

    if (d)
      _and.push({
        _or: d,
      });
  }

  if (onlyEnabledListingByAutomationId) {
    _and.push({
      listingConnection: {
        listing: whereLegacyListingIsExcluded(onlyEnabledListingByAutomationId),
      },
    });
  }

  const source_bool: source_bool_exp = {
    status: {
      _neq: status !== 'deleted' ? 'inactive' : undefined,
      _eq: status === 'deleted' ? 'inactive' : undefined,
    },
  };

  // On GL teams, show manual reservatons (sourceId is null)
  if (GL) {
    _and.push({
      _or: [
        {
          source: source_bool,
        },
        {
          sourceId: { _is_null: true },
        },
      ],
    });
  } else {
    _and.push({
      source: source_bool,
    });
  }

  const bookingChannels = bc?.filter((b) => isUUID(b) === !GL);

  return ensure<reservation_bool_exp>({
    journalEntries: GL ? {} : undefined,
    paymentLines:
      (dashboard === 'owner' && !GL) || includeLines ? {} : undefined,
    transactionLines: // PRO-9357
      // (dashboard === 'owner' && GL) || includeLines ? {} : undefined,
      includeLines ? {} : undefined,
    tenantId: { _eq: tenantId },
    paidStatus: paidStatus
      ? {
          _eq: paidStatus === 'paid' ? 'paid' : undefined,
          _neq: paidStatus === 'unpaid' ? 'paid' : undefined,
        }
      : undefined,
    connection: connectionIds?.length
      ? {
          id: { _in: connectionIds },
          status: whereConnectionStatusDefault,
        }
      : { status: whereConnectionStatusDefault },
    channel: bookingChannels?.length
      ? {
          id: GL ? undefined : { _in: bookingChannels },
          channelRef: GL ? { _in: bookingChannels } : undefined, // use channelRef for GL
        }
      : undefined,
    _and: _and.length ? _and : undefined,
    status:
      status === 'deleted'
        ? {
            _neq: 'inquiry',
          }
        : status
          ? {
              _eq: status === 'canceled' ? 'cancelled' : status,
            }
          : search
            ? undefined
            : {
                _is_null: false,
                _nin: ['inquiry', 'inactive'],
              },
  });
}

const getDateBool = (
  date: ReservationsFilterParams['date'],
  dateType: ReservationsFilterParams['dateType']
): reservation_bool_exp[] | undefined => {
  const startDate = date?.[0]
    ? day(date[0]).startOf('day').yyyymmdd()
    : undefined;
  const endDate = date?.[1] ? day(date[1]).add(1, 'day').yyyymmdd() : undefined;

  if (!startDate) return undefined;

  const start = startDate;
  const end = endDate || day(start).add(1, 'day').yyyymmdd();

  if (dateType === 'checkIn') {
    return [
      {
        checkIn: { _gte: start, _lt: end },
      },
    ];
  }
  if (dateType === 'checkOut') {
    return [
      {
        checkOut: { _gte: start, _lt: end },
      },
    ];
  }
  if (dateType === 'bookedAt') {
    return [
      {
        bookedAt: { _gte: start, _lt: end },
      },
    ];
  }
  if (dateType === 'cancelledAt') {
    return [
      {
        status: { _eq: 'cancelled' },
        cancelledAt: { _gte: start, _lt: end },
      },
    ];
  }

  return [
    {
      checkIn: { _gte: start, _lt: end },
    },
    {
      checkOut: { _gte: start, _lt: end },
    },
    {
      checkIn: { _lt: start },
      checkOut: { _gte: end },
    },
  ];
};

const getListingBool = ({
  dashboard,
  listingIds,
  listingStatus,
  listingCollectionId,
  GL,
  ownerPortalContactId,
}: {
  dashboard: Props['dashboard'];
  listingIds: Props['listingIds'];
  listingStatus: Props['listingStatus'];
  listingCollectionId: Props['listingCollectionId'];
  GL: Props['GL'];
  ownerPortalContactId: Props['ownerPortalContactId'];
}): reservation_bool_exp | undefined => {
  if (
    !(listingIds?.length || listingStatus || listingCollectionId) &&
    !ownerPortalContactId
  )
    return undefined;

  const listing: listing_bool_exp | undefined =
    listingCollectionId || listingStatus || dashboard === 'owner'
      ? {
          collectionId: listingCollectionId
            ? { _eq: listingCollectionId }
            : undefined,
          ownerships:
            dashboard === 'owner' && !GL
              ? {
                  newOwnerId: { _eq: ownerPortalContactId },
                }
              : undefined,
          ownershipPeriods:
            dashboard === 'owner' && GL
              ? {
                  members: {
                    contact: {
                      userAccess: {
                        contactId: { _eq: ownerPortalContactId },
                      },
                    },
                  },
                }
              : undefined,
          _and: listingStatus
            ? listingStatus === 'active'
              ? [whereListingIsActive()] // GL & LEGACY
              : [
                  !GL
                    ? {
                        calculatedStatus: { _eq: 'inactive' },
                      }
                    : whereGlListingIsInactive(undefined, day().yyyymmdd()), // only GL
                ]
            : undefined,
        }
      : undefined;

  return {
    _or: [
      {
        listingConnection: {
          listingId: listingIds?.length ? { _in: listingIds } : undefined,
          listing,
        },
      },
      {
        listingId: listingIds?.length ? { _in: listingIds } : undefined,
        listing,
      },
    ],
  };
};
